import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { t } from '../../services/i18nService';
import styles from './tableOfContents.scss';
import settingsReader from '../../store/readHelpers/SettingsReader';
import sessionReader from '../../store/readHelpers/SessionReader';
import TOCSection from './TOCSection';
import scrollingService from '../../services/ScrollingService';
import viewReadyTracker from '../../services/ViewReadyTracker';
import { getFilteredListOfQuestions } from '../../services/TOCService';
import uiService from '../../services/UIService';
import tableOfContentsSelector from '../../selectors/tableOfContents/TableOfContentsSelector';
import tocActions from '../../actions/TocActions';
import marketplacePropertiesService from '../../services/MarketplacePropertiesService';
import responseReader from '../../store/readHelpers/ResponseReader';
import TOCFilters from './TOCFilters';

const { filterTypes } = tocActions;

const TableOfContents = (props) => {
    const {
        assessment,
        dispatch,
        flaggedItems,
        isNavigationAllowed,
        isPreloading,
        questionsFilter,
        response,
        settings,
        onReady,
        ui,
        showTOCFiltersInMainLandmark,
    } = props;

    const hasMandatoryQuestions = useCallback(() => {
        if (marketplacePropertiesService.isMandatoryQuestionEnabled()) {
            if (!assessment?.sections?.length) {
                return false;
            }
            return assessment.sections.some(section =>
                section.questions?.some(question => question.isMandatoryQuestion)
            );
        }
        return false;
    }, [assessment]);

    const hasUnansweredMandatory = useCallback(() => {
        if (marketplacePropertiesService.isMandatoryQuestionEnabled()) {
            if (!response) return false;

            const mandatoryQuestions = (assessment.sections || [])
                .flatMap(sec => (Array.isArray(sec.questions) ? sec.questions : []))
                .filter(q => q && (q.isMandatoryQuestion === true || q.isMandatoryQuestion === 'true'));

            if (mandatoryQuestions.length === 0) return false;

            return mandatoryQuestions.some((q) => {
                return !responseReader.isQuestionDone(q);
            });
        }
        return false;
    }, [assessment, response]);

    const getAccesabilityData = useCallback(() => {
        switch (questionsFilter) {
        case filterTypes.ALL:
            return { id: 'tabpanel-all', ariaLabelledby: 'tab-all' };
        case filterTypes.NOT_ATTEMPTED:
            return { id: 'tabpanel-not-attempted', ariaLabelledby: 'tab-not-attempted' };
        case filterTypes.FLAGGED:
            return { id: 'tabpanel-flagged', ariaLabelledby: 'tab-flagged' };
        case filterTypes.MANUALLY_SELECTED:
            return { id: 'tabpanel-manually-selected', ariaLabelledby: 'tab-manually-selected' };
        case filterTypes.MANUALLY_SELECTED_NOT_ATTEMPTED:
            return { id: 'tabpanel-manually-selected-not-attempted', ariaLabelledby: 'tab-manually-selected-not-attempted' };
        default:
            return { id: '', ariaLabelledby: '' };
        }
    }, [questionsFilter]);

    useEffect(() => {
        // This component can be used as a main view or as a sub component, so we need two ways of handling ready reporting.
        if (onReady) {
            onReady(true);
        } else {
            viewReadyTracker.getReporter()(true);
        }
        scrollingService.scrollToTop();
    }, []);

    const getSections = () => {
        if (!assessment || !assessment.sections || !assessment.sections.length) {
            return null;
        }

        const currentlyShown = getFilteredListOfQuestions(assessment.sections, questionsFilter);

        return currentlyShown.map(({ questions, sectionIndex }) => {
            const section = assessment.sections[sectionIndex];

            return (<TOCSection
                allowNavigation={isNavigationAllowed}
                currentlyShown={questions}
                dispatch={dispatch}
                flaggedItems={flaggedItems}
                key={sectionIndex}
                response={response}
                section={section}
                sectionIndex={sectionIndex}
                settings={settings}
                ui={ui}
            />);
        });
    };

    if (uiService.shouldUseSimplifiedSummaryPage()) {
        return null;
    }


    const settingsBarStyle = settings.settingsBar && settings.settingsBar.active ? { marginTop: '3.5em' } : null;
    const classList = [styles.overview];
    if (showTOCFiltersInMainLandmark) {
        classList.push(styles.overviewV5);
    }
    const isDeliveringPage = uiService.getRoute() === '/delivering';
    const notAttempted = hasUnansweredMandatory();
    const renderWarning = () => {
        if (isDeliveringPage && notAttempted) {
            return (
                <div className={styles.mandatoryDeliveringWarning}>
                    <div className={styles.mandatoryDeliveringWarningRow}>
                        <i className={`fa fa-exclamation-circle ${styles.customWarningDeliveryIcon}`} aria-hidden="true" />
                        <span className={styles.mandatoryWarningDeliveryText}>
                            <span dangerouslySetInnerHTML={{ __html: t('mandatory.delivering.warning.text') }} />
                        </span>
                    </div>
                </div>
            );
        }
        if (!isDeliveringPage && hasMandatoryQuestions()) {
            return (
                <div className={styles.mandatoryWarning}>
                    <div className={styles.mandatoryWarningRow}>
                        <i className={`fa fa-exclamation-triangle ${styles.customWarningIcon}`} aria-hidden="true" />
                        <span className={styles.mandatoryWarningText}>
                            <span dangerouslySetInnerHTML={{ __html: t('mandatory.delivering.tableHeader.warning.text') }} />
                        </span>
                    </div>
                </div>
            );
        }
        return null;
    };
    if (settingsReader.hasSettingValue(sessionReader.printParamNames.INSERT_PAGE_BREAK_BEFORE_QUESTION, true)) {
        classList.push('page-break-after');
    }

    const scrollableContent = (
        <React.Fragment>
            <h3 className="sr-only">
                { t('testOverview.srOnly.questionListHeader') }
            </h3>
            {renderWarning()}
            <table
                id={getAccesabilityData().id}
                aria-labelledby={getAccesabilityData().ariaLabelledby}
                className={styles.body}
            >
                { getSections() }
            </table>
        </React.Fragment>
    );

    return (
        <div className={classList.join(' ')} role="main" style={settingsBarStyle}>
            {showTOCFiltersInMainLandmark && <TOCFilters className={
                `${styles.filtersInMainLandmark}${isPreloading ? ` ${styles.filtersPreloading}` : ''}`
            }
            />}
            {showTOCFiltersInMainLandmark
                ? <div className={styles.scrollableContent}>{scrollableContent}</div>
                : scrollableContent
            }
        </div>
    );
};

TableOfContents.propTypes = {
    assessment: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    flaggedItems: PropTypes.array.isRequired,
    isNavigationAllowed: PropTypes.bool.isRequired,
    isPreloading: PropTypes.bool.isRequired,
    onReady: PropTypes.func,
    questionsFilter: PropTypes.string,
    response: PropTypes.object.isRequired,
    settings: PropTypes.object.isRequired,
    ui: PropTypes.object,
    showTOCFiltersInMainLandmark: PropTypes.bool.isRequired,
};

const mapStateToProps = state => tableOfContentsSelector.selectProps(state);
export default connect(mapStateToProps)(TableOfContents);
